import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { ArrowBack, Refresh } from '@mui/icons-material';
import { AuthCard } from '@components/common/AuthCard/AuthCard';
import { CustomTextField } from '@components/common/CustomTextField/CustomTextField';
import { CustomAlert } from '@components/common/CustomAlert/CustomAlert';
import { OTPInput } from '@components/common/OTPInput/OTPInput';
import { CustomLink } from '@components/common/CustomLink/CustomLink';
import { PrimaryButton } from '@components/buttons/PrimaryButton';
import { ThemeToggle } from '@components/common/ThemeToggle/ThemeToggle';
import { useTheme } from '@mui/material';
import { useAppTheme } from '@contexts/ThemeContext';
import { useAuth } from '@store/authStore';
import httpClient from '@utils/httpClient';

const ResetPassword: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const navigate = useNavigate();
  const { logout } = useAuth();
  const { isDarkMode } = useAppTheme();
  const theme = useTheme();

  const steps = ['Enter Email', 'Verify OTP', 'Set New Password'];

  useEffect(() => {
    console.log('ResetPassword: Component mounted');
    console.log('ResetPassword: Current URL:', window.location.href);
    console.log('ResetPassword: Current pathname:', window.location.pathname);

    // Add a small delay to check if there are any redirects happening
    const timer = setTimeout(() => {
      console.log('ResetPassword: After 100ms - URL:', window.location.href);
      console.log('ResetPassword: After 100ms - pathname:', window.location.pathname);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value !== '' && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }

    if (e.key === 'Enter') {
      handleOTPSubmit();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text');
    const pasteArray = paste.slice(0, 6).split('');

    const newOtp = [...otp];
    pasteArray.forEach((digit, index) => {
      if (index < 6 && /\d/.test(digit)) {
        newOtp[index] = digit;
      }
    });
    setOtp(newOtp);
  };

  const handleEmailSubmit = async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const data = await httpClient.post('/forgot-password', { email: email.trim() });

      if (data.success) {
        setSuccess(data.message);
        setActiveStep(1);
      } else {
        setError(data.error || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      // Error message is already formatted by httpClient
      setError(error instanceof Error ? error.message : 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const handleOTPSubmit = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const data = await httpClient.post('/verify-otp', {
        email: email.trim(),
        otp: otpString
      });

      if (data.success) {
        setSuccess('OTP verified successfully');
        setActiveStep(2);
      } else {
        setError(data.error || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      // Error message is already formatted by httpClient
      setError(error instanceof Error ? error.message : 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!newPassword.trim()) {
      setError('Please enter a new password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const data = await httpClient.post('/reset-password', {
        email: email.trim(),
        otp: otp.join(''),
        newPassword: newPassword
      });

      if (data.success) {
        setSuccess('Password has been reset successfully');

        // Clear any existing auth data and redirect to login
        setTimeout(() => {
          logout(); // Clear auth data first
          navigate('/login');
        }, 2000);
      } else {
        setError(data.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      // Error message is already formatted by httpClient
      setError(error instanceof Error ? error.message : 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="body1" color="text.secondary" style={{ marginBottom: '1.5rem', fontSize: '1rem' }}>
              Enter your email address and we'll send you an OTP to reset your password.
            </Typography>

            <CustomTextField
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleEmailSubmit();
                }
              }}
            />

            <PrimaryButton
              onClick={handleEmailSubmit}
              loading={loading}
              fullWidth
              variant="primary"
              sx={{
                marginTop: '2rem',
                backgroundColor: '#2196F3', // Solid blue background like in image
                color: 'white', // White text for high contrast
                border: 'none',
                minHeight: { xs: '48px', sm: '52px' },
                py: { xs: 1.5, sm: 2 },
                fontSize: { xs: '1rem', sm: '1.1rem' },
                '&:hover': {
                  backgroundColor: '#1976D2', // Darker blue on hover
                  transform: 'none',
                  boxShadow: 'none'
                },
                '&:disabled': {
                  backgroundColor: '#BDBDBD', // Gray when disabled
                  color: 'white',
                  border: 'none',
                },
                '&:focus': {
                  outline: 'none',
                  boxShadow: 'none'
                },
                '&:focus-visible': {
                  outline: 'none',
                  boxShadow: 'none'
                },
                '&:active': {
                  backgroundColor: '#1565C0', // Even darker blue on active
                  transform: 'none'
                }
              }}
            >
              Send OTP
            </PrimaryButton>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography
              variant="body1"
              color="text.secondary"
              style={{
                marginBottom: '2rem',
                textAlign: 'center',
                lineHeight: 1.5,
                fontSize: '1rem'
              }}
            >
              We have sent a 6-digit OTP to {email}. Please enter it below.
            </Typography>

            <Box style={{ marginBottom: '2rem' }}>
              <OTPInput
                value={otp}
                onChange={handleOtpChange}
                onKeyDown={handleOtpKeyDown}
                onPaste={handleOtpPaste}
                disabled={loading}
              />
            </Box>

            <PrimaryButton
              onClick={handleOTPSubmit}
              loading={loading}
              fullWidth
              variant="primary"
              sx={{
                marginBottom: '2rem',
                backgroundColor: '#2196F3', // Solid blue background like in image
                color: 'white', // White text for high contrast
                border: 'none',
                minHeight: { xs: '48px', sm: '52px' },
                py: { xs: 1.5, sm: 2 },
                fontSize: { xs: '1rem', sm: '1.1rem' },
                '&:hover': {
                  backgroundColor: '#1976D2', // Darker blue on hover
                  transform: 'none',
                  boxShadow: 'none'
                },
                '&:disabled': {
                  backgroundColor: '#BDBDBD', // Gray when disabled
                  color: 'white',
                  border: 'none',
                },
                '&:focus': {
                  outline: 'none',
                  boxShadow: 'none'
                },
                '&:focus-visible': {
                  outline: 'none',
                  boxShadow: 'none'
                },
                '&:active': {
                  backgroundColor: '#1565C0', // Even darker blue on active
                  transform: 'none'
                }
              }}
            >
              Verify OTP
            </PrimaryButton>

            <Box textAlign="center">
              <CustomLink
                onClick={() => {
                  setActiveStep(0);
                  setOtp(['', '', '', '', '', '']);
                }}
                startIcon={<Refresh sx={{ fontSize: '1rem' }} />}
              >
                Didn't receive OTP? Try again
              </CustomLink>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="body1" color="text.secondary" style={{ marginBottom: '2rem', fontSize: '1rem' }}>
              Create a new password for your account
            </Typography>

            <Box style={{ marginTop: '1.5rem' }}>
              <CustomTextField
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
                showPasswordToggle
                onTogglePassword={() => setShowNewPassword(!showNewPassword)}
                showPassword={showNewPassword}
              />
            </Box>

            <Box style={{ marginTop: '1.75rem' }}>
              <CustomTextField
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handlePasswordReset();
                  }
                }}
                showPasswordToggle
                onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
                showPassword={showConfirmPassword}
              />
            </Box>

            <PrimaryButton
              onClick={handlePasswordReset}
              loading={loading}
              fullWidth
              variant="primary"
              sx={{
                marginTop: '2rem',
                backgroundColor: '#2196F3', // Solid blue background like in image
                color: 'white', // White text for high contrast
                border: 'none',
                minHeight: { xs: '48px', sm: '52px' },
                py: { xs: 1.5, sm: 2 },
                fontSize: { xs: '1rem', sm: '1.1rem' },
                '&:hover': {
                  backgroundColor: '#1976D2', // Darker blue on hover
                  transform: 'none',
                  boxShadow: 'none'
                },
                '&:disabled': {
                  backgroundColor: '#BDBDBD', // Gray when disabled
                  color: 'white',
                  border: 'none',
                },
                '&:focus': {
                  outline: 'none',
                  boxShadow: 'none'
                },
                '&:focus-visible': {
                  outline: 'none',
                  boxShadow: 'none'
                },
                '&:active': {
                  backgroundColor: '#1565C0', // Even darker blue on active
                  transform: 'none'
                }
              }}
            >
              Reset Password
            </PrimaryButton>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <ThemeToggle />
      <AuthCard
        title="Reset Password"
        showBackButton
        onBackClick={() => navigate('/login')}
        maxWidth="md"
      >

        {/* Progress Stepper */}
        <Stepper activeStep={activeStep} style={{ marginBottom: '2rem' }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }, // Reduced text size
                    fontWeight: 500,
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error Alert */}
        {error && (
          <div style={{ marginBottom: '2rem' }}>
            <CustomAlert type="error" message={error} />
          </div>
        )}

        {/* Success Alert */}
        {success && (
          <div style={{ marginBottom: '2rem' }}>
            <CustomAlert type="success" message={success} />
          </div>
        )}

        {/* Step Content */}
        {renderStepContent()}

        {/* Back to Login Link */}
        <Box textAlign="center" sx={{ marginTop: '2rem', marginBottom: '1rem' }}>
          <CustomLink
            onClick={() => {
              logout(); // Logout user first
              navigate('/login');
            }}
            startIcon={<ArrowBack sx={{ fontSize: '1rem' }} />}
            sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
          >
            Back to Sign In
          </CustomLink>
        </Box>
      </AuthCard>
    </>
  );
};

export default ResetPassword;
